/**
 * Sending a sound this app synthesises to a device the operator picked.
 *
 * Twilio routes its own sounds for us; everything the CRM makes itself - the
 * ring, the test beep - comes out of an `AudioContext`, and an `AudioContext`
 * plays into the system default with no say in the matter. The way round it is
 * to end the graph in a `MediaStreamAudioDestinationNode`, hang that stream off
 * an `<audio>` element, and set the element's sink: `HTMLMediaElement.setSinkId`
 * is the same capability Twilio tests for in `isOutputSelectionSupported`, so
 * the whole feature has ONE support story rather than one per sound.
 *
 * (`AudioContext.setSinkId` exists in recent Chrome and would be shorter. It is
 * not used, because it is Chrome-only: taking it would mean the speaker picker
 * worked in Firefox and the ring picker did not, on the same screen, with
 * nothing to explain the difference.)
 */

/** `setSinkId` is not in every lib.dom this repo compiles against. */
type SinkCapableElement = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

/** Where a graph should end, and how to take it down again. */
export interface AudioSink {
  node: AudioNode;
  dispose: () => void;
}

/**
 * Can this browser send sound anywhere but the system default?
 *
 * Read off the prototype rather than off an element, so it can be asked before
 * anything is built - the pickers grey their output lists out with it.
 */
export function canRouteAudio(): boolean {
  return (
    typeof HTMLMediaElement !== "undefined" &&
    typeof (HTMLMediaElement.prototype as SinkCapableElement).setSinkId === "function"
  );
}

/**
 * The end of the graph, routed to `deviceId` when there is one and when the
 * browser will have it.
 *
 * Falls back to the default output on every failure, and that is deliberate:
 * every caller here is making a sound whose job is to be HEARD, so the wrong
 * speaker beats no speaker. The fallback is silent because there is nothing the
 * operator could do about it mid-ring anyway - the picker is where an
 * unsupported browser says so, once.
 */
export async function openAudioSink(ctx: AudioContext, deviceId: string): Promise<AudioSink> {
  const plain: AudioSink = { node: ctx.destination, dispose: () => {} };
  if (!deviceId || !canRouteAudio()) return plain;

  const dest = ctx.createMediaStreamDestination();
  const el = document.createElement("audio") as SinkCapableElement;
  el.srcObject = dest.stream;
  el.setAttribute("aria-hidden", "true");
  el.style.display = "none";
  document.body.appendChild(el);

  const drop = () => {
    el.pause();
    el.srcObject = null;
    el.remove();
  };

  try {
    await el.setSinkId?.(deviceId);
    // After the sink, not before: playing first would put one moment of audio
    // through the old device on every ring.
    await el.play();
  } catch {
    drop();
    return plain;
  }

  return {
    node: dest,
    dispose: () => {
      drop();
      dest.disconnect();
    },
  };
}
